/**
 * Integrytas Group - Base App (MOBILE)
 * Page: EDS.EMAIL.LOG — mobile-native (READ-ONLY).
 *
 * Touch-first counterpart of src/pages/eds/EdsEmailLogPage.tsx. A DISTINCT
 * presentation (KPI summary + dense card list + bottom-sheet detail with an
 * event timeline) over the SAME data layer: reads via apiDb (eds_email_log
 * joined to eds_campaign / eds_email_template, plus the eds_email_event rows
 * for a selected send), resolving delivery-status and event-type codes to
 * translated names through their _tl tables against the ACTIVE locale (§8/§17.B4).
 * It is read-only — no writes — so it never touches the Common Routine. §16:
 * never the desktop grid re-hosted in a phone viewport.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Mail } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobilePageComponentProps } from '../mobile_page_components';
import type { EdsEmailLogRow, EdsEmailEventRow } from '../../types/eds';

// Map the internal lead/pill class strings onto the Direction-C primitive tones.
const LEAD_TONE_KEY: Record<string, MobileLeadTone> = {
  'm-l-green': 'green', 'm-l-blue': 'blue', 'm-l-amber': 'amber', 'm-l-red': 'red', 'm-l-slate': 'slate',
};
const PILL_TONE_KEY: Record<string, MobilePillTone> = {
  'm-p-ok': 'ok', 'm-p-info': 'info', 'm-p-wait': 'wait', 'm-p-no': 'no', 'm-p-you': 'you',
};

// Delivery-status → mobile status-pill class. Visual only; the *label* is always
// the localized value from eds_delivery_status_tl, never the raw code.
const STATUS_PILL: Record<string, string> = {
  queued: 'm-p-wait',
  sent: 'm-p-info',
  delivered: 'm-p-ok',
  soft_bounce: 'm-p-wait',
  hard_bounce: 'm-p-no',
  spam_complaint: 'm-p-no',
  failed: 'm-p-no',
};

// Delivery-status → card lead accent.
const STATUS_LEAD: Record<string, string> = {
  queued: 'm-l-amber',
  sent: 'm-l-blue',
  delivered: 'm-l-green',
  soft_bounce: 'm-l-amber',
  hard_bounce: 'm-l-red',
  spam_complaint: 'm-l-red',
  failed: 'm-l-red',
};

// Event-type → mobile status-pill class (timeline). Label from eds_event_type_tl.
const EVENT_PILL: Record<string, string> = {
  sent: 'm-p-info',
  delivered: 'm-p-ok',
  opened: 'm-p-ok',
  clicked: 'm-p-info',
  bounced: 'm-p-no',
  complained: 'm-p-no',
  unsubscribed: 'm-p-wait',
};

interface TlTextRow {
  code: string;
  language_id: string;
  display_text: string;
}

/** Collapse per-code _tl rows to a single label for the active locale (fallback EN_US). */
function buildLabelMap(rows: TlTextRow[], locale: string): Record<string, string> {
  const byCode: Record<string, TlTextRow[]> = {};
  for (const r of rows) (byCode[r.code] ??= []).push(r);
  const out: Record<string, string> = {};
  for (const [code, tls] of Object.entries(byCode)) {
    const exact = tls.find((tl) => tl.language_id === locale);
    const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
    out[code] = exact?.display_text ?? fb?.display_text ?? code;
  }
  return out;
}

type CampaignTl = { language_id: string; campaign_name?: string };
type RawLogRow = EdsEmailLogRow & {
  eds_campaign?: { eds_campaign_tl?: CampaignTl[] } | null;
  eds_email_template?: { internal_code: string; from_name_default: string } | null;
};

const MAX_ROWS = 500;

export function MobileEdsEmailLogPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch).
  const activeLocale = useActiveLocale();
  const fmtDateTime = useFormatDateTime();

  const [rows, setRows] = useState<EdsEmailLogRow[]>([]);
  const [statusLabels, setStatusLabels] = useState<Record<string, string>>({});
  const [eventLabels, setEventLabels] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string>('all');
  const [selected, setSelected] = useState<EdsEmailLogRow | null>(null);
  const [events, setEvents] = useState<EdsEmailEventRow[]>([]);
  const [eventsBusy, setEventsBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // §17.B2: delivery-status + event-type labels from their _tl tables (no
      // hardcoded code→label lists).
      const [{ data: statusTl }, { data: eventTl }] = await Promise.all([
        apiDb.from('eds_delivery_status_tl').select('delivery_status_code, language_id, display_text'),
        apiDb.from('eds_event_type_tl').select('event_type_code, language_id, display_text'),
      ]);
      setStatusLabels(
        buildLabelMap(
          ((statusTl ?? []) as { delivery_status_code: string; language_id: string; display_text: string }[])
            .map((r) => ({ code: r.delivery_status_code, language_id: r.language_id, display_text: r.display_text })),
          activeLocale,
        ),
      );
      setEventLabels(
        buildLabelMap(
          ((eventTl ?? []) as { event_type_code: string; language_id: string; display_text: string }[])
            .map((r) => ({ code: r.event_type_code, language_id: r.language_id, display_text: r.display_text })),
          activeLocale,
        ),
      );

      const { data, error: e } = await apiDb
        .from('eds_email_log')
        .select(`
          log_id, campaign_id, template_id, recipient_id, to_email_address,
          language_id, delivery_status_code, provider_message_id,
          queued_at, sent_at, delivered_at, failed_reason, created_at,
          eds_campaign!left(eds_campaign_tl!left(campaign_name, language_id)),
          eds_email_template!left(internal_code, from_name_default)
        `)
        .order('queued_at', { ascending: false })
        .limit(MAX_ROWS);
      if (e) throw e;

      const flat: EdsEmailLogRow[] = ((data ?? []) as RawLogRow[]).map((r) => {
        const ctls = r.eds_campaign?.eds_campaign_tl ?? [];
        const camTl =
          ctls.find((c) => c.language_id === activeLocale) ??
          ctls.find((c) => c.language_id === 'EN_US') ??
          ctls[0];
        return {
          ...r,
          campaign_name:
            camTl?.campaign_name ??
            (r.campaign_id ? r.campaign_id.slice(0, 8) + '…' : t('mobile.eds_email_log.transactional', 'Transactional')),
          template_internal_code: r.eds_email_template?.internal_code ?? r.template_id,
          // §17.B4: resolved template name, never the raw internal_code / uuid.
          template_display_name:
            r.eds_email_template?.from_name_default ?? r.eds_email_template?.internal_code ?? r.template_id,
        };
      });
      setRows(flat);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Load the engagement timeline for the selected send.
  useEffect(() => {
    if (!selected) { setEvents([]); return; }
    let cancelled = false;
    setEventsBusy(true);
    void apiDb
      .from('eds_email_event')
      .select('*')
      .eq('log_id', selected.log_id)
      .order('occurred_at')
      .then(({ data }) => {
        if (cancelled) return;
        setEvents((data ?? []) as EdsEmailEventRow[]);
        setEventsBusy(false);
      });
    return () => { cancelled = true; };
  }, [selected]);

  const statusLabel = (code: string) => statusLabels[code] ?? code;
  const eventLabel = (code: string) => eventLabels[code] ?? code;

  // §17.B5: status facet chips derived from the loaded rows + the status lookup.
  const statusOptions = useMemo(() => {
    const codes = [...new Set(rows.map((r) => r.delivery_status_code).filter(Boolean))].sort();
    return codes.map((code) => ({ code, label: statusLabel(code) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, statusLabels]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet !== 'all' && r.delivery_status_code !== statusFacet) return false;
      if (!q) return true;
      return (
        r.to_email_address.toLowerCase().includes(q) ||
        (r.campaign_name ?? '').toLowerCase().includes(q) ||
        (r.template_display_name ?? '').toLowerCase().includes(q) ||
        (r.template_internal_code ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  // KPI summary tiles (total / delivered / failed) over the loaded window.
  const kpis = useMemo(() => {
    const failedCodes = new Set(['hard_bounce', 'spam_complaint', 'failed']);
    let delivered = 0;
    let failed = 0;
    for (const r of rows) {
      if (r.delivery_status_code === 'delivered') delivered += 1;
      if (failedCodes.has(r.delivery_status_code)) failed += 1;
    }
    return { total: rows.length, delivered, failed };
  }, [rows]);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.eds_email_log.title', 'Email Log')}
        description={t('mobile.eds_email_log.description', 'Every email sent, its delivery status and engagement timeline.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.eds_email_log.kpi_total', 'Sends')}</div>
          <div className="m-kpi-v">{kpis.total}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.eds_email_log.kpi_delivered', 'Delivered')}</div>
          <div className="m-kpi-v">{kpis.delivered}</div>
        </div>
        <div className={'m-kpi' + (kpis.failed > 0 ? ' m-kpi-alert' : '')}>
          <div className="m-kpi-l">{t('mobile.eds_email_log.kpi_failed', 'Failed')}</div>
          <div className="m-kpi-v">{kpis.failed}</div>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.eds_email_log.search', 'Search email / campaign')}
          aria-label={t('mobile.eds_email_log.search', 'Search email / campaign')}
        />
      </div>

      <MobileFilterChips
        chips={[
          { value: 'all', label: t('mobile.eds_email_log.all', 'All') },
          ...statusOptions.map((o) => ({ value: o.code, label: o.label })),
        ]}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.eds_email_log.none', 'No emails match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.log_id}
              lead={<Mail />}
              leadTone={LEAD_TONE_KEY[STATUS_LEAD[r.delivery_status_code] ?? 'm-l-slate']}
              title={r.to_email_address}
              subtitle={`${r.campaign_name} · ${fmtDateTime(r.queued_at)}`}
              trailing={
                <MobileStatusPill tone={PILL_TONE_KEY[STATUS_PILL[r.delivery_status_code] ?? 'm-p-info']}>
                  {statusLabel(r.delivery_status_code)}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.eds_email_log.detail', 'Email detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Mail size={20} /></span>
              <span>
                <div className="m-nm">{selected.to_email_address}</div>
                <div className="m-em">{selected.campaign_name}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.eds_email_log.status', 'Status')}
                value={
                  <MobileStatusPill tone={PILL_TONE_KEY[STATUS_PILL[selected.delivery_status_code] ?? 'm-p-info']}>
                    {statusLabel(selected.delivery_status_code)}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.eds_email_log.template', 'Template')}
                value={selected.template_display_name ?? selected.template_internal_code}
              />
              <MobileDetailRow label={t('mobile.eds_email_log.queued_at', 'Queued')} value={fmtDateTime(selected.queued_at)} />
              <MobileDetailRow label={t('mobile.eds_email_log.sent_at', 'Sent')} value={selected.sent_at ? fmtDateTime(selected.sent_at) : '—'} />
              <MobileDetailRow label={t('mobile.eds_email_log.delivered_at', 'Delivered')} value={selected.delivered_at ? fmtDateTime(selected.delivered_at) : '—'} />
              {selected.failed_reason && (
                <MobileDetailRow
                  label={t('mobile.eds_email_log.failed_reason', 'Failure')}
                  value={<span style={{ color: 'var(--color-danger)' }}>{selected.failed_reason}</span>}
                />
              )}
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                groups={[
                  { key: 'template', title: t('mobile.eds_email_log.related.template', 'Template'),
                    items: selected.template_display_name || selected.template_internal_code
                      ? [{ id: 'template',
                           label: selected.template_display_name ?? selected.template_internal_code ?? '',
                           sub: selected.template_internal_code ?? undefined }]
                      : [] },
                ]}
              />
            </MobileContentCard>

            {/* Engagement timeline (eds_email_event) */}
            <div className="m-fieldcard">
              <div className="m-field"><span className="m-k">{t('mobile.eds_email_log.events', 'Timeline')}</span>
                <span className="m-v">{eventsBusy ? t('common.loading', 'Loading…') : `${events.length}`}</span>
              </div>
              {!eventsBusy && events.length === 0 && (
                <div className="m-empty">{t('mobile.eds_email_log.no_events', 'No events recorded')}</div>
              )}
              {events.map((ev) => (
                <div key={ev.event_id} className="m-field">
                  <span className="m-k">
                    <span className={'m-pill ' + (EVENT_PILL[ev.event_type_code] ?? 'm-p-info')}>
                      {eventLabel(ev.event_type_code)}
                    </span>
                  </span>
                  <span className="m-v">{fmtDateTime(ev.occurred_at)}</span>
                </div>
              ))}
            </div>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('mobile.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
